import { commandsCtx, editorViewCtx, parserCtx } from '@milkdown/core'
import type { Ctx } from '@milkdown/ctx'
import { clearTextInCurrentBlockCommand } from '@milkdown/kit/preset/commonmark'
import { Slice } from '@milkdown/prose/model'
import type { ResolvedPos } from '@milkdown/prose/model'
import type { BlockEditFeatureConfig } from '@milkdown/crepe/feature/block-edit'
import type { TopBarGroup } from '../formattingTopBar'
import { snippetReferenceMarkdown } from './snippetToken'

/**
 * A closed block slice is safe only in blocks that are direct document
 * children. Depth alone keeps it out of lists, blockquotes and table cells; a
 * code block is a root block, but `replaceSelection` tears the fence in half
 * rather than rejecting the slice, so refuse that too.
 */
function positionIsInRootBlock(position: ResolvedPos): boolean {
  return position.depth === 1 && !position.parent.type.spec.code
}

/**
 * The token goes through the editor's OWN parser, not `insertText`, so it lands
 * as a `snippetBlock` node in the same transaction. The slice is CLOSED
 * (openStart/openEnd 0) because `Slice.maxOpen` would merge the block into the
 * paragraph at the caret, demoting it to inline text.
 */
export function insertSnippetReference(ctx: Ctx, id: string): boolean {
  const markdown = snippetReferenceMarkdown(id)
  if (!markdown) return false
  const view = ctx.get(editorViewCtx)
  if (!view.editable) return false
  const { $from, $to } = view.state.selection
  if (!positionIsInRootBlock($from) || !positionIsInRootBlock($to)) return false

  try {
    const parsed = ctx.get(parserCtx)(markdown)
    if (!parsed || parsed.content.size === 0) return false
    view.dispatch(
      view.state.tr.replaceSelection(new Slice(parsed.content, 0, 0))
    )
    view.focus()
    return true
  } catch (error) {
    console.debug('insertSnippetReference: skipped (invalid selection)', error)
    return false
  }
}

/**
 * Toolbar / slash-menu glyph: `<>`, matching the `CodeXml` icon the sidebar, the
 * tab bar and the list rows all use for Snippets. Crepe sanitizes + `innerHTML`s
 * the string, and index.css paints `.top-bar-item svg` with `fill` — not
 * `stroke` — so this is a FILLED 24px path, like Crepe's own icons, rather than
 * lucide's stroked one.
 */
export const SNIPPET_ICON = `
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <title>Insert snippet</title>
    <path d="M9.4 16.6 4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
  </svg>
`

type BuildMenu = NonNullable<BlockEditFeatureConfig['buildMenu']>
type MenuBuilder = Parameters<BuildMenu>[0]

/**
 * The Insert-group toolbar button. Added from the call site, into the group
 * `buildFormattingTopBar` returns, so the shared toolbar builder stays free of
 * snippet-specific parameters.
 */
export function addSnippetToolbarItem(
  insert: TopBarGroup,
  onRun: (ctx: Ctx) => void
): void {
  insert.addItem('snippet', {
    icon: SNIPPET_ICON,
    // Opens a dialog rather than toggling doc state, so it is never "on".
    active: () => false,
    onRun,
  })
}

/**
 * `/snippet` in the block-edit slash menu. Crepe filters its menu on the item
 * label, so the label IS the command the user types. `onRun` clears the typed
 * `/snippet` first, exactly as Crepe's own items do, so the query text isn't
 * left sitting in front of the inserted block.
 */
export function addSnippetSlashItem(
  builder: MenuBuilder,
  onRun: (ctx: Ctx) => void
): void {
  // `getGroup` THROWS when the group is absent, and this callback runs inside
  // Crepe's menu build — an escaping throw would take the whole menu down.
  let group
  try {
    group = builder.getGroup('advanced')
  } catch {
    group = builder.addGroup('advanced', 'Advanced')
  }
  group.addItem('snippet', {
    label: 'Snippet',
    icon: SNIPPET_ICON,
    onRun: (ctx) => {
      ctx.get(commandsCtx).call(clearTextInCurrentBlockCommand.key)
      onRun(ctx)
    },
  })
}
